import { useApolloClient } from '@apollo/client';
import { get, map, size } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  trackArticleCarouselArrow,
  trackRelatedProject,
} from '../../../common/analytics/analyticsTracker';
import { SRP } from '../../../common/analytics/sourceTypes';
import { SCREEN_TYPE } from '../../../common/helpers/constants';
import { isDesktop } from '../../../common/helpers/utility/screenHelper';
import { mediaQuery } from '../../../common/styles/styleUtil';
import { ProjectItem } from '../../../queries/fragments/project';
import { ProjectFilterParams } from '../../../services/search';
import { projectManager } from '../../../services/projectManager';
import {
  WholeState,
  WholeState as CommonState,
} from '../../../store/common/type';
import {
  updateLoadingProjectCarousel,
  updateProjectsItems,
  updateProjectTotalCount,
} from '../../../store/search/slice';
import { WholeState as SearchState } from '../../../store/search/type';
import BlockLoader from '../../loader/BlockLoader';
import SimpleCarousel from '../../simpleCarousel/SimpleCarousel';
import SimpleProjectCard, {
  ProjectCardClickEvent,
} from '../../simpleProjectCard/SimpleProjectCard';
import NoResultMessage from './NoResultMessage';
import { AnalyticsProject } from '../../../common/analytics/interfaces/analyticsProject';

export const ProjectListCarouselWrapper = styled.div`
  margin-top: 32px;

  @media (${mediaQuery.tablet}) {
    padding-left: 20px;
  }

  @media (${mediaQuery.mobile}) {
    margin-top: 24px;
  }
`;

export const PER_PAGE = 20;
const DESKTOP_SLIDES_TO_SCROLL = 3;
const TABLET_SLIDES_TO_SCROLL = 2;
const MOBILE_SLIDES_TO_SCROLL = 1;

const ProjectListContainer: React.FC<Props> = ({ advanceSearchQueries }) => {
  const loading = useSelector<SearchState, boolean>((state) =>
    state.search ? state.search.loadingProjectsCarousel : false,
  );
  const totalCount = useSelector<SearchState, number>((state) =>
    state.search ? state.search.projectsTotalCount : 0,
  );
  const projectItems = useSelector<SearchState, ProjectItem[]>((state) =>
    state.search ? state.search.projectsItems : [],
  );
  const deviceType = useSelector<CommonState, string>((state) =>
    state.common ? state.common.deviceType : 'desktop',
  );
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );

  const client = useApolloClient();
  const dispatch = useDispatch();
  const updateTotalCount = (count: number) =>
    dispatch(updateProjectTotalCount(count));
  const updateLoading = (loading: boolean) =>
    dispatch(updateLoadingProjectCarousel(loading));
  const updateItems = (productItems: ProjectItem[]) =>
    dispatch(updateProjectsItems(productItems));

  const getMoreProjectItems = async () => {
    try {
      const { items, totalCount } = await projectManager(
        projectItems.length,
        advanceSearchQueries,
        client,
        PER_PAGE,
      );

      updateTotalCount(totalCount);
      updateItems([...projectItems, ...items]);
    } catch (e) {
      console.log('Error when fetch project items:', e);
    } finally {
      updateLoading(false);
    }
  };

  const changeSlides = (to: number) => {
    if (to === 0) {
      return;
    }

    trackArticleCarouselArrow(userInfo, SRP.PAGE);

    if (
      (isDesktop(deviceType) &&
        to + DESKTOP_SLIDES_TO_SCROLL >= size(projectItems) &&
        to + DESKTOP_SLIDES_TO_SCROLL < totalCount) ||
      (deviceType === SCREEN_TYPE.mobile &&
        to + MOBILE_SLIDES_TO_SCROLL >= size(projectItems) &&
        to + MOBILE_SLIDES_TO_SCROLL < totalCount) ||
      (deviceType === SCREEN_TYPE.tablet &&
        to + TABLET_SLIDES_TO_SCROLL >= size(projectItems) &&
        to + TABLET_SLIDES_TO_SCROLL < totalCount)
    ) {
      getMoreProjectItems();
    }
  };

  const handleClickProjectCard = (event: ProjectCardClickEvent) => {
    const projectInfo: AnalyticsProject = {
      id: event.id,
      propertyType: event.propertyTypes,
      developer: event.developerName,
      title: event.title,
      location: event.location,
      priceTypes: event.priceTypes,
      transportations: event.transportations,
    };
    trackRelatedProject(userInfo, projectInfo, SRP.PAGE);
  };

  return (
    <ProjectListCarouselWrapper data-test={'project-list-carousel'}>
      {!loading && projectItems.length !== 0 && (
        <SimpleCarousel
          data-test="simple-carousel"
          onChangeSlides={changeSlides}
          slidesToScrollConfig={{
            desktop: DESKTOP_SLIDES_TO_SCROLL,
            smallDesktop: TABLET_SLIDES_TO_SCROLL,
            tablet: TABLET_SLIDES_TO_SCROLL,
            small: MOBILE_SLIDES_TO_SCROLL,
          }}
        >
          {map(projectItems, (item) => {
            const {
              id,
              title,
              link,
              featuredImage,
              propertyType,
              priceTypes,
              location,
              developer,
              logo,
              transportations,
            } = item;

            return (
              <SimpleProjectCard
                key={id}
                id={id}
                title={title}
                link={link}
                featuredImageUrl={get(featuredImage, 'url', '')}
                location={location}
                propertyTypes={propertyType}
                priceTypes={priceTypes}
                transportations={transportations}
                developerName={get(developer, 'name', '')}
                logoUrl={get(logo, 'image.url', '')}
                logoBgColor={get(logo, 'background_color', '')}
                onClickProjectCard={handleClickProjectCard}
              />
            );
          })}
        </SimpleCarousel>
      )}
      {!loading && projectItems.length === 0 && <NoResultMessage />}
      {loading && projectItems.length === 0 && <BlockLoader />}
    </ProjectListCarouselWrapper>
  );
};

interface Props {
  advanceSearchQueries: ProjectFilterParams;
}

export default ProjectListContainer;
